'use client';

/**
 * API namespace `userAddresses`.
 * Verbatim extraction from frontend/src/lib/api-client.ts (L9494-L9551).
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-2, T-EXPORT, T-IMPORTS (see ledger).
 */

import { apiFetch } from '../core/fetch-client';

import type {
  AuthResponse, ApiError, RegisterRequest, LoginRequest, PublicUser,
  DashboardData, FeatureFlags, UpdateProfileInput, ChangePasswordInput,
  Setup2FAResult, Verify2FAResult, Login2FAResponse,
  ForgotPasswordInput, ResetPasswordInput, VerifyEmailInput,
  NetworkTree, WalletData, ConferencesData, CreateConferenceInput,
  CreateInterviewBody, InterviewCreatedResponse, InterviewListResponse,
  InviteBody, InviteResponse, JoinBody, JoinResponse,
  DownlineResult, QualificationResult, IDeposit, PayoutResult,
  ReconciliationResult,
  UserDepositStatus,
  CreditBalanceResponse, CreditHistoryResponse,
  CreditPurchaseRequest, CreditPurchaseResponse, CreditTransactionsResponse,
  SellerRegistrationRequest, SellerProfileResponse,
  FulfillmentConfigRequest, OnboardingStepRequest,
  SellerDashboardResponse, PublicStoreResponse,
  StoreBranding, StoreBrandingUpdate,
  SellerPayoutRequestBody, SellerPayoutBalanceResponse,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  OrderCreateRequest, OrderCreateRequestUnion,
  OrderResponse, OrderListResponse, ShipOrderRequest,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  NetworkStats, WalletTransaction, WalletStats,
  RoomParticipantStat, ConferenceStats, NetworkNode,
  AnnouncementDto, CreateAnnouncementBody, UpdateAnnouncementBody,
  KycApplicationDto, Paginated,
  DepositDto, DepositStatus,
  DepositNewPayload, PayoutDto, PayoutStatus, PayoutStatusChangePayload,
  AuditListQuery, PaginatedAuditEntries, AuditChainVerificationResult,
  KycApplication, KycReviewStatus, KycDocumentType,
  KycFatfInput, GeoLocationOption, ZodType, Landing,
  AccountingNetwork, AccountingNetworkBreakdown, AccountingSummary,
  PayoutRequestBody, PushSubscribeInput, PushUnsubscribeInput,
  AcademyTrack, AcademyCourseStatus, AcademyCourseDifficulty,
  AcademyCourse, AcademyCourseWithMeta, AcademyCatalogResponse,
  AcademyModuleListItem, AcademyModuleDetail, AcademyModuleResource,
  AcademyEnrollmentSummary, AcademyMyCourse, AcademyMyCertificate,
  AcademyVerifyResult, AcademyMarkProgressResponse, AcademyAdminCourse,
  IUserCertification, EntrepreneurProfile, EntrepreneurProfileInput,
  DiscoveryResponse,
  AccountingUserBalances, AccountingLedgerEntry,
  AccountBalanceResult, TrialBalanceRow, TrialBalanceResult,
  ReconciliationReport, ReconciliationStatusResponse,
  ReconciliationHistoryResponse, AccountingPostResult,
  UnsignedTransaction, SubmitTransactionResult,
  TransactionStatusResult, PrepareTransactionBody,
  SubmitTransactionBody, AwaitingSignaturePayout,
  WelcomeMessageResponse, WelcomeMessageInput,
  SavedAddressResponse, SavedAddressInput,
  ForbiddenWordResponse, ForbiddenWordInput,
  PublicRoomInfo, PublicRoomBySlugInfo,
} from '../core/shared-types';

import type {
  AdminUserDto, AdminUserDetailDto, AdminPurgeUserResultDto,
  FeatureFlagDto, AdminConferenceDto, LandingLimits, AdminMetricsDto,
  DepositChainInfo, UserDepositDto,
  CircuitState, CircuitBreakerMetricsDto,
  CircuitBreakerResetResultDto, CircuitBreakerStateChangePayload,
  TransactionLedgerEntryDto, TransactionDto,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  ReconciliationStatus,
} from '../core/types';

import type {
  ConferenceRoomResponse, InterviewRoomResponse,
  ReservationAvailabilitySlot, ReservationAvailability,
  ReservationSlot, BookingInput, ReservationCreated, ReservationListItem,
  DirectMessageDTO, SendDirectMessageInput, UploadedDirectFileDTO,
} from '../core/interfaces';

export const userAddresses = {
  /**
   * GET /api/me/addresses — list the user's saved addresses (address book).
   *
   * Returns SavedAddressResponse[] (max 10 per user — backend caps it).
   */
  async list(): Promise<SavedAddressResponse[]> {
    return apiFetch<SavedAddressResponse[]>('/me/addresses');
  },
  /**
   * POST /api/me/addresses — add a new address to the address book.
   * Requires recent 2FA.
   *
   * Returns 400 ADDRESS_BOOK_FULL if the user already has 10 addresses.
   */
  async add(input: SavedAddressInput): Promise<SavedAddressResponse> {
    return apiFetch<SavedAddressResponse>('/me/addresses', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  /**
   * PUT /api/me/addresses/:savedAddressId — edit an existing address.
   * Requires recent 2FA.
   *
   * Returns 404 SAVED_ADDRESS_NOT_FOUND if the id doesn't exist in the
   * user's address book.
   */
  async update(savedAddressId: string, input: SavedAddressInput): Promise<SavedAddressResponse> {
    return apiFetch<SavedAddressResponse>(`/me/addresses/${savedAddressId}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },
  /**
   * DELETE /api/me/addresses/:savedAddressId — remove an address from
   * the address book. Requires recent 2FA.
   *
   * Returns 204 No Content on success.
   */
  async remove(savedAddressId: string): Promise<void> {
    await apiFetch<void>(`/me/addresses/${savedAddressId}`, {
      method: 'DELETE',
    });
  },
};
